import {
  DataTableActionsCell,
  DataTableAuditCell,
  DataTableCellContent,
  DataTableHeaderCell,
} from '@frontend-platform/ui-lib';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend-platform/ui-lib/ui';
import { formatRelativeTime } from '@frontend-platform/util-format';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { formatAbsoluteTime, formatAnalysisStartTime, formatListeningWindow } from '../lib/format';
import { getRunStatusTooltip } from '../lib/run-status-tooltip';
import { isCompletedStatus, isUnsuccessfulStatus } from '../lib/status';
import { type CalibrationRun, getCriteriaCount } from '../lib/types';
import { BuildBadge } from './build-badge';
import { RunRowActions } from './run-row-actions';
import { RunStatusBadge } from './run-status-badge';

/**
 * Explains a terminal status the badge alone can't: why a run failed, or that
 * a cancel was deliberate. Renders nothing for statuses that need no gloss.
 */
function RunStatusInfo({ run }: { run: CalibrationRun }) {
  const tooltip = getRunStatusTooltip(run);
  if (!tooltip) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info aria-label="Status details" className="ml-1 h-3 w-3 shrink-0 text-muted-foreground" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-left">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

interface RunListColumnOptions {
  /**
   * Resolves an environment slug to its display label. Injected rather than
   * imported because EP labels come from the bot's environment ladder, which
   * only a hook can read.
   */
  getEnvLabel: (value: string | undefined | null) => string;
}

export function buildRunListColumns({
  getEnvLabel,
}: RunListColumnOptions): ColumnDef<CalibrationRun>[] {
  return [
    {
      accessorKey: 'runName',
      // Flex column: size === 150 is treated as "no explicit width" by the
      // DataTable, so this absorbs leftover container width.
      size: 150,
      minSize: 220,
      enableSorting: false,
      header: () => <DataTableHeaderCell title="Run name" />,
      // Only a COMPLETED run has a report to open — scheduled, in-progress,
      // failed and canceled runs render as plain text rather than a link, so
      // the row can't navigate to an empty detail page. Mirrors aistudio's
      // TableView.vue `isRowDisabled` / `ENABLED_STATUS` gate.
      cell: ({ row }) => (
        <DataTableCellContent flex>
          {isCompletedStatus(row.original.status) ? (
            <Link
              to={ROUTES.RUN_DETAIL}
              params={{ runId: row.original.runId }}
              className="truncate font-medium text-foreground hover:text-[#3f3f46] hover:underline"
            >
              {row.original.runName}
            </Link>
          ) : (
            <span className="cursor-not-allowed truncate font-medium text-muted-foreground">
              {row.original.runName}
            </span>
          )}
        </DataTableCellContent>
      ),
    },
    {
      id: 'status',
      size: 140,
      enableSorting: false,
      header: () => <DataTableHeaderCell title="Status" />,
      cell: ({ row }) => (
        <DataTableCellContent flex>
          <RunStatusBadge status={row.original.status} />
          {row.original.status === 'SCHEDULED' ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 shrink-0 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="text-left">
                <ul className="list-disc pl-4">
                  <li>
                    Listening window -{' '}
                    {formatListeningWindow(row.original.startTime, row.original.endTime)}
                  </li>
                  <li>
                    Sample rate - {row.original.scheduledDataPayload?.sampleRate}% of conversations
                  </li>
                  <li>Analysis start time - {formatAnalysisStartTime(row.original.endTime)}</li>
                </ul>
              </TooltipContent>
            </Tooltip>
          ) : null}
          <RunStatusInfo run={row.original} />
        </DataTableCellContent>
      ),
    },
    {
      id: 'criteria',
      size: 120,
      enableSorting: false,
      header: () => (
        <DataTableHeaderCell>
          <span className="inline-flex items-center gap-1">
            Criteria
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>Number of evaluation criteria selected</TooltipContent>
            </Tooltip>
          </span>
        </DataTableHeaderCell>
      ),
      cell: ({ row }) => {
        const criteriaLines =
          row.original.selectedMetrics?.flatMap((g) => {
            const category = g.category.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
            return g.metrics.map((m) => `${category}: ${m.displayName}`);
          }) ?? [];
        return (
          <DataTableCellContent flex>
            <span className="inline-flex items-center gap-1">
              {getCriteriaCount(row.original)}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="text-left">
                  {criteriaLines.length ? (
                    <div className="flex flex-col gap-0.5">
                      {criteriaLines.map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </div>
                  ) : (
                    'No criteria'
                  )}
                </TooltipContent>
              </Tooltip>
            </span>
          </DataTableCellContent>
        );
      },
    },
    {
      id: 'recommendations',
      size: 160,
      enableSorting: false,
      header: () => <DataTableHeaderCell title="Recommendations" />,
      cell: ({ row }) => (
        <DataTableCellContent flex>
          {isUnsuccessfulStatus(row.original.status)
            ? '-'
            : (row.original.recommendationCount ?? 0)}
        </DataTableCellContent>
      ),
    },
    {
      // The column names the evaluated env; the build tag and its status come
      // from the run's own manifestBuild / releaseActive / releaseAction fields,
      // so a run keeps showing the build it actually evaluated even after the
      // env has been promoted past it.
      id: 'environment',
      size: 240,
      enableSorting: false,
      header: () => <DataTableHeaderCell title="Environment" />,
      cell: ({ row }) => (
        <DataTableCellContent className="gap-2" flex>
          <span>{getEnvLabel(row.original.evaluationEnvironment ?? row.original.env)}</span>
          <BuildBadge run={row.original} />
        </DataTableCellContent>
      ),
    },
    {
      id: 'updatedAt',
      size: 220,
      accessorFn: (row) => Number(row.updatedAt),
      header: ({ column }) => <DataTableHeaderCell column={column} title="Last run by" />,
      cell: ({ row }) => {
        const user = row.original.updatedBy ?? row.original.createdBy;
        const relative = formatRelativeTime(Number(row.original.updatedAt));
        const absolute = formatAbsoluteTime(row.original.updatedAt);
        const when = [relative, absolute].filter(Boolean).join(' · ');
        return (
          <DataTableAuditCell
            relativeTime={relative}
            updatedLabel="Last run"
            updated={{
              when,
              user: {
                displayName: user.name,
                avatarName: user.name,
                email: user.email,
                seed: user.id || row.original.runId,
              },
            }}
          />
        );
      },
    },
    {
      id: 'actions',
      size: 56,
      enableSorting: false,
      header: () => <DataTableHeaderCell />,
      cell: ({ row }) => (
        <DataTableActionsCell>
          <RunRowActions run={row.original} />
        </DataTableActionsCell>
      ),
    },
  ];
}
